import { useState } from 'react'
import { Trans } from '@lingui/react/macro'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TuneIcon from '@mui/icons-material/Tune'
import VideocamIcon from '@mui/icons-material/Videocam'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import FolderSharedIcon from '@mui/icons-material/FolderShared'

const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '12px !important',
  '&:before': { display: 'none' },
  boxShadow: 'none',
}

const listSx = { m: 0, pl: 2.5, '& li': { mb: 0.5 } }

const guideItems = [
  {
    id: 'before',
    icon: <TuneIcon fontSize="small" color="primary" />,
    title: <Trans>Before your consultation</Trans>,
    content: (
      <Box component="ul" sx={listSx}>
        <li>
          <Trans>Ensure you have a stable internet connection (Wi-Fi or 4G).</Trans>
        </li>
        <li>
          <Trans>Check that your camera and microphone are working properly.</Trans>
        </li>
        <li>
          <Trans>Choose a quiet, well-lit environment.</Trans>
        </li>
        <li>
          <Trans>Have your medical documents ready if needed (prescriptions, test results, etc.).</Trans>
        </li>
        <li>
          <Trans>Log in to your account a few minutes before the scheduled time.</Trans>
        </li>
      </Box>
    ),
  },
  {
    id: 'during',
    icon: <VideocamIcon fontSize="small" color="primary" />,
    title: <Trans>During the consultation</Trans>,
    content: (
      <Box component="ul" sx={listSx}>
        <li>
          <Trans>You will be placed in a waiting room until the doctor starts the session.</Trans>
        </li>
        <li>
          <Trans>Once the doctor is ready, you will be redirected automatically to the consultation.</Trans>
        </li>
        <li>
          <Trans>You can share your screen or documents during the session if needed.</Trans>
        </li>
        <li>
          <Trans>If you experience technical issues, try refreshing the page or checking your connection.</Trans>
        </li>
      </Box>
    ),
  },
  {
    id: 'after',
    icon: <FactCheckIcon fontSize="small" color="primary" />,
    title: <Trans>After the consultation</Trans>,
    content: (
      <Box component="ul" sx={listSx}>
        <li>
          <Trans>Your medical documents (prescriptions, reports) will be available in the Documents section of your account.</Trans>
        </li>
        <li>
          <Trans>You can download or print your documents at any time.</Trans>
        </li>
        <li>
          <Trans>If you have follow-up questions, contact your doctor or our support team.</Trans>
        </li>
      </Box>
    ),
  },
  {
    id: 'data',
    icon: <FolderSharedIcon fontSize="small" color="primary" />,
    title: <Trans>Accessing your data</Trans>,
    content: (
      <Box component="ul" sx={listSx}>
        <li>
          <Trans>All your medical documents, prescriptions, and consultation reports are available in the Documents section of your account.</Trans>
        </li>
        <li>
          <Trans>You can download your documents in PDF format at any time.</Trans>
        </li>
        <li>
          <Trans>Your consultation history is accessible from the Consultations section.</Trans>
        </li>
        <li>
          <Trans>To update your personal information, go to the Profile section.</Trans>
        </li>
      </Box>
    ),
  },
]

export function TeleconsultationGuideSection() {
  const [expanded, setExpanded] = useState<string | false>('before')

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 4, bgcolor: 'grey.50', mb: 4 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        <Trans>Teleconsultation guide</Trans>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <Trans>Everything you need to know for a successful video consultation.</Trans>
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {guideItems.map((item) => (
          <Accordion key={item.id} expanded={expanded === item.id} onChange={handleChange(item.id)} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {item.icon}
                <Typography variant="body2" fontWeight={600}>
                  {item.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" component="div">
                {item.content}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  )
}
